/**
 * Sound, vibration, and system-notification alerts for recipe step timers.
 *
 * Browsers only allow audio after a user gesture (strictly enforced on iOS),
 * so `primeTimerAlarm` must be called from the tap that starts a timer; the
 * alarm later reuses that unlocked AudioContext. Every call here is
 * best-effort: an unsupported API just means one fewer alert channel.
 */

type AudioContextCtor = typeof AudioContext;

let audioContext: AudioContext | null = null;

const getAudioContextCtor = (): AudioContextCtor | undefined =>
  typeof window === 'undefined'
    ? undefined
    : window.AudioContext ?? (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext;

export const primeTimerAlarm = () => {
  try {
    const Ctor = getAudioContextCtor();
    if (!Ctor) return;
    if (!audioContext) audioContext = new Ctor();
    if (audioContext.state === 'suspended') void audioContext.resume();
    // A silent blip inside the gesture fully unlocks playback on iOS.
    const gain = audioContext.createGain();
    gain.gain.value = 0;
    const osc = audioContext.createOscillator();
    osc.connect(gain).connect(audioContext.destination);
    osc.start();
    osc.stop(audioContext.currentTime + 0.01);
  } catch {
    // No Web Audio — the toast and vibration still fire.
  }
};

export const playTimerAlarm = () => {
  try {
    navigator.vibrate?.([300, 150, 300, 150, 300]);
  } catch {
    // ignore
  }
  try {
    const ctx = audioContext;
    if (!ctx) return;
    if (ctx.state === 'suspended') void ctx.resume();
    const start = ctx.currentTime + 0.05;
    // Three pairs of short beeps.
    for (let i = 0; i < 6; i++) {
      const t = start + Math.floor(i / 2) * 0.8 + (i % 2) * 0.22;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.4, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.2);
    }
  } catch {
    // ignore
  }
};

// Android Chrome exposes Notification but forbids its constructor without a
// service worker (which this app doesn't register), so asking for permission
// there would be a prompt with no payoff.
const notificationsSupported = () =>
  typeof window !== 'undefined' && 'Notification' in window &&
  !/Android/i.test(navigator.userAgent);

export const requestTimerNotificationPermission = () => {
  try {
    if (notificationsSupported() && Notification.permission === 'default') {
      void Notification.requestPermission();
    }
  } catch {
    // Some browsers throw when requesting outside supported contexts.
  }
};

/** Shown only while the page is hidden; in-app, the toast covers it. */
export const showTimerSystemNotification = (title: string, body: string | undefined, tag: string) => {
  try {
    if (!notificationsSupported() || Notification.permission !== 'granted') return;
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') return;
    const n = new Notification(title, { body, tag, icon: '/logo192.png' });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {
    // ignore
  }
};
